import { del, get as apiGet, patch, post } from "../../services/api";
import { v4 as uuidv4 } from "uuid";
import { SYSTEM_MODES } from "./createAppSlice";
import { datadogRum } from "@datadog/browser-rum";
import { trackEvent, ANALYTICS_EVENTS } from "@analytics";
import { queryClient } from "src/lib/react-query/queryClient";
import { FETCH_QUERY_KEYS } from "src/queries/consts";
import { CACHE_DURATION, getApiCollectionName } from "@consts";
import { getDialogErrorContent, GENERIC_ERROR_MESSAGES } from "src/components/collections/consts";
import assistantsService from "src/queries/services/assistants";
import { POLICIES_TYPES } from "src/components/collections/policies/PoliciesTable.consts";

const createCollectionsSlice = (set, get) => ({
  isPageLoading: true,
  isCollectionLoading: true,
  dialogError: "",
  isDialogLoading: false,
  collections: null,
  collectionsLanguages: [],
  defaultCollectionsLanguages: null,
  // collection config
  collectionConfig: {},
  originalCollectionConfig: {},
  currentCollectionConfig: {},
  collectionSpec: null,
  selectedCollection: {},
  isUpdatingCollection: false,
  collectionConfigChanged: false,
  isSavingChanges: false,
  collectionName: "",
  validationResults: {},
  fetchingValidationResults: false,
  hasFetchedInitialValidationResults: false,
  failedToUpdateDraft: false,
  lastCollectionCreatedKey: null,
  collectionBreadCrumbs: [],
  ignoreUrlChange: false,
  connectionResults: {},
  languages: {},
  currentSelectedLanguage: null,
  setCurrentSelectedLanguage: value => set({ currentSelectedLanguage: value }),
  hasAddedFilter: false,
  // settings config
  settingsConfigChanged: false,
  initialChannelSettings: null,
  currentChannelSettings: null,
  draftChannelSettings: null,
  // configuration settings (languages for phone channel)
  configurationSettingsChanged: false,
  setConfigurationSettingsChanged: value => set({ configurationSettingsChanged: value }),
  setIgnoreUrlChange: value => set({ ignoreUrlChange: value }),
  setLastCollectionCreatedKey: value => set({ lastCollectionCreatedKey: value }),
  addToCollectionBreadCrumbs: (value) => {
    const { collectionBreadCrumbs } = get();
    set({ collectionBreadCrumbs: [...collectionBreadCrumbs, value] });
    set({ ignoreUrlChange: true });
  },
  removeLastItemFromCollectionBreadCrumbs: () => {
    const { collectionBreadCrumbs } = get();
    set({ collectionBreadCrumbs: collectionBreadCrumbs.slice(0, -1) });
  },
  clearCollectionBreadCrumbs: () => {
    set({ collectionBreadCrumbs: [] });
  },
  setCollectionName: value => set({ collectionName: value }),
  setIsUpdatingCollection: value => set({ isUpdatingCollection: value }),
  setCollectionConfigChanged: value => set({ collectionConfigChanged: value }),
  setSettingsConfigChanged: value => set({ settingsConfigChanged: value }),
  setCurrentChannelSettings: value => set({ currentChannelSettings: value }),
  setInitialChannelSettings: value => set({ initialChannelSettings: value }),
  setDraftChannelSettings: value => set({ draftChannelSettings: value }),
  setDialogLoading: value => set({ isDialogLoading: value }),
  setDialogError: value => set({ dialogError: value }),
  setPageLoading: value => set({ isPageLoading: value }),
  setCollectionLoading: value => set({ isCollectionLoading: value }),
  setSettingsPhoneNumbersChanged: value => set({ settingsPhoneNumbersChanged: value }),
  setHasAddedFilter: value => set({ hasAddedFilter: value }),
  setCollections: collections => {
    const { systemMode } = get();

    const hasDraftRef = Object.keys(collections?.draftConfigRef || {}).length > 0;
    const configRef = hasDraftRef ? collections.draftConfigRef : collections.configRef;

    set({
      isPageLoading: false,
      collections: {
        specRef: collections.specRef,
        configRef,
        originalConfig: collections.configRef,
        currentConfig: systemMode === SYSTEM_MODES.EDIT ? configRef : collections.configRef
      }
    });
  },

  setCollection: (collection, collectionInstanceKey) => {
    const { systemMode, draft, policiesData: existingPoliciesData, getPoliciesData } = get();
    const { draftConfig, config, spec, validationResults, languages: liveLanguages, draftLanguages, utteranceGrouping, generalGrouping } = collection;
    const [currentPoliciesObject, policiesType] = getPoliciesData(collection);
    const collectionConfig = Object.keys(draftConfig || {}).length ? draftConfig : config;
    const collectionLanguages = draftLanguages?.default ? draftLanguages : liveLanguages;
    const currentPolicies = (systemMode === SYSTEM_MODES.EDIT && draft ? currentPoliciesObject?.policies?.draft : currentPoliciesObject?.policies?.active);
    const currentPoliciesSpec = (systemMode === SYSTEM_MODES.EDIT && draft ? currentPoliciesObject?.spec?.draft : currentPoliciesObject?.spec?.active);
    const existingLocalPolicies = existingPoliciesData?.policies?.local;
    const isSameCollectionInstance = existingPoliciesData?.collectionInstanceKey === collectionInstanceKey;

    let policiesDataObject;
    if (currentPoliciesObject) {
      policiesDataObject = {
        policiesType,
        collectionInstanceKey,
        selectedPolicy: {
          live: null,
          draft: null,
          current: null,
          local: null
        },
        policies: {
          live: currentPoliciesObject?.policies?.active || currentPoliciesObject?.policies?.live || [],
          draft: draft && currentPoliciesObject?.policies?.draft ? currentPoliciesObject?.policies?.draft : currentPolicies,
          current: currentPolicies,
          local: existingLocalPolicies && existingLocalPolicies.length > 0 && existingPoliciesData.policiesType === policiesType && isSameCollectionInstance ? existingLocalPolicies : currentPolicies
        },
        spec: {
          live: currentPoliciesObject?.spec?.active || currentPoliciesObject?.spec?.live || [],
          draft: currentPoliciesSpec,
          current: currentPoliciesSpec
        },
        metadata: {
          tableKeys: currentPoliciesSpec.map(policySpec => policySpec.key),
          metaKeys: currentPoliciesSpec.map(policySpec => policySpec.metadataTableId)
        }
      };
    }
    set({
      utteranceGrouping,
      generalGrouping,
      policiesData: policiesDataObject,
      languages: {
        live: liveLanguages,
        draft: draftLanguages,
        current: systemMode === SYSTEM_MODES.EDIT ? collectionLanguages : liveLanguages
      },
      collectionConfig,
      currentCollectionConfig: systemMode === SYSTEM_MODES.EDIT ? collectionConfig : config,
      originalCollectionConfig: config,
      collectionSpec: spec,
      selectedCollection: { ...(systemMode === SYSTEM_MODES.EDIT ? collectionConfig : config), spec },
      isCollectionLoading: false,
      isPageLoading: false,
      validationResults: {
        live: {},
        draft: validationResults,
        current: systemMode === SYSTEM_MODES.EDIT && draft ? validationResults : {}
      }
    });
  },
  getValidationResults: async () => {
    const { selectedAssistant, systemMode, draft } = get();
    try {
      set({ fetchingValidationResults: true });
      const validationResults = await queryClient.fetchQuery({
        queryKey: [FETCH_QUERY_KEYS.VALIDATION_RESULTS, selectedAssistant.assistantId],
        queryFn: async () => await apiGet({ endpoint: `v2/assistants/collections/${selectedAssistant.assistantId}/all/validations` }),
        staleTime: CACHE_DURATION.ONE_MINUTE
      });
      set({
        validationResults: {
          live: {},
          draft: validationResults,
          current: systemMode === SYSTEM_MODES.EDIT && draft ? validationResults : {}
        },
        couldNotSetUpAssistant: false
      });

      return validationResults;
    } catch (error) {
      console.error("Failed to load validations", error);
      set({ couldNotSetUpAssistant: true });
      return { error: true };
    } finally {
      set({ fetchingValidationResults: false, hasFetchedInitialValidationResults: true });
    }
  },
  getCollectionOptions: async (collectionName) => {
    const { selectedAssistant } = get();
    try {
      return await apiGet({ endpoint: `v2/assistants/collections/${selectedAssistant.assistantId}/${getApiCollectionName(collectionName)}/spec-refs` });
    } catch (e) {
      console.error("Failed to load collection options", e);
      return { error: true };
    }
  },
  resetCollectionsData: () => {
    set({
      collectionConfig: {},
      originalCollectionConfig: {},
      collectionSpec: null,
      selectedCollection: {},
      collections: {},
      languages: { live: {}, draft: {}, current: {} },
      validationResults: { live: {}, draft: {}, current: {} },
      hasFetchedInitialValidationResults: false,
      previewAssistantId: null,
      policiesData: {
        policies: {
          live: [],
          draft: [],
          current: [],
          local: []
        },
        selectedPolicy: {
          live: null,
          draft: null,
          current: null,
          local: null
        }
      },
      policiesConfigChanged: false
    });
  },
  createCollection: async ({ specKey, displayKey, description, collectionName, createFromRef }) => {
    const {
      selectedAssistant,
      userData,
      updateSliceState,
      pageName,
      collections
    } = get();
    set({ isDialogLoading: true });
    const lastCollectionCreatedKey = uuidv4();
    try {
      const {
        specRef,
        configRef,
        draftConfigRef,
        draft,
        validationResults,
        updatedCollection
      } = await post({
        endpoint: `v2/assistants/collections/${selectedAssistant.assistantId}/${getApiCollectionName(collectionName)}`,
        data: {
          display_key: displayKey,
          description,
          key: lastCollectionCreatedKey,
          spec_key: specKey,
          userData: { name: userData.name, email: userData.email }
        },
        headers: { pageName }
      });
      let updatedDraftConfigRef = collections.configRef || [];
      updatedDraftConfigRef = [...updatedDraftConfigRef, updatedCollection];
      updateSliceState({ specRef, configRef: updatedDraftConfigRef, draftConfigRef: updatedDraftConfigRef, draft });
      set({
        validationResults: {
          live: {},
          draft: validationResults,
          current: validationResults
        }
      });

      if (createFromRef) {
        set({ lastCollectionCreatedKey });
      }
      return true;
    } catch (err) {
      console.error("Error occurred while creating collection", err);
      set({ dialogError: getDialogErrorContent("create", collectionName), isDialogLoading: false });
      return false;
    }
  },
  deleteCollection: async (collection) => {
    const {
      selectedAssistant,
      userData,
      updateSliceState,
      collectionName,
      pageName
    } = get();
    set({ isDialogLoading: true });
    try {
      const {
        specRef,
        configRef,
        draftConfigRef,
        draft,
        validationResults
      } = await del({
        endpoint: `v2/assistants/collections/${selectedAssistant.assistantId}/${getApiCollectionName(collectionName)}`,
        data: {
          userData: { name: userData.name, email: userData.email },
          ...collection
        },
        headers: { pageName }
      });
      set({
        validationResults: {
          live: {},
          draft: validationResults,
          current: validationResults
        }
      });
      updateSliceState({ specRef, configRef, draftConfigRef, draft });
      return true;
    } catch (err) {
      console.error("Error occurred while deleting collection", err);
      set({ dialogError: getDialogErrorContent("delete", collectionName), isDialogLoading: false });
      return false;
    }
  },
  editCollection: async (collection) => {
    const {
      pageName,
      selectedAssistant,
      userData,
      updateSliceState,
      collectionName,
      collections
    } = get();
    set({ isDialogLoading: true });
    try {
      const {
        specRef,
        configRef,
        draftConfigRef,
        draft,
        validationResults,
        updatedCollection
      } = await patch({
        endpoint: `v2/assistants/collections/${selectedAssistant.assistantId}/${getApiCollectionName(collectionName)}`,
        data: {
          userData: { name: userData.name, email: userData.email },
          ...collection
        },
        headers: { pageName }
      });
      let updatedDraftConfigRef = collections.configRef;
      updatedDraftConfigRef = updatedDraftConfigRef.map(c => c.key === collection.key ? updatedCollection : c);
      set({
        validationResults: {
          live: {},
          draft: validationResults,
          current: validationResults
        }
      });
      updateSliceState({ specRef, configRef: updatedDraftConfigRef, draftConfigRef: updatedDraftConfigRef, draft });
      return true;
    } catch (err) {
      console.error("Error occurred while editing collection", err);
      set({ dialogError: getDialogErrorContent("edit", collectionName), isDialogLoading: false });
      return false;
    }
  },
  updateCollection: async (collection, updateCollectionMutation) => {
    const {
      selectedAssistant,
      userData,
      setSelectedCollectionConfig,
      collectionName,
      pageName,
      infoPopovers,
      setBuildDraftResponse,
      policiesData,
      policiesConfigChanged,
      closeSpeechPlayer,
      getPoliciesData
    } = get();
    const { configuration: { key: collectionKey }, key: collectionInstanceKey } = collection;
    set({
      isSavingChanges: true,
      isUpdatingCollection: true
    });

    const { spec, ...instanceConfig } = collection;
    try {
      const payload = {
        instance_config: instanceConfig,
        userData: { name: userData.name, email: userData.email }
      };

      if (policiesConfigChanged && policiesData?.policies?.local) {
        if (policiesData.policiesType === POLICIES_TYPES.SCREENING) {
          payload.pbx = {
            policies: policiesData.policies.local
          };
        }
        if (policiesData.policiesType === POLICIES_TYPES.SCHEDULING) {
          payload.schedulingPolicies = {
            policies: policiesData.policies.local
          };
        }
      }

      const params = {
        selectedAssistantId: selectedAssistant.assistantId,
        collectionName,
        collectionKey,
        collectionInstanceKey,
        pageName,
        payload
      };

      const result = await updateCollectionMutation.mutateAsync(params);

      if (!result) {
        set({
          isSavingChanges: false,
          isUpdatingCollection: false,
          isDialogLoading: false
        });
        queryClient.refetchQueries({ queryKey: [FETCH_QUERY_KEYS.DRAFT] });
        return true;
      }

      const {
        updatedCollection,
        draft,
        validationResults,
        spec
      } = result;
      const branchDeployerResponse = await apiGet({ endpoint: `/assistants/${selectedAssistant.assistantId}/build/status?branchName=${draft?.branchName}` });
      setBuildDraftResponse(branchDeployerResponse);

      setSelectedCollectionConfig(updatedCollection, spec);
      set({ draft });

      const updObj = {
        collectionConfig: updatedCollection,
        currentCollectionConfig: updatedCollection,
        isSavingChanges: false,
        isUpdatingCollection: false,
        infoPopovers: { ...infoPopovers, draftExpiration: true },
        validationResults: {
          live: {},
          draft: validationResults,
          current: validationResults
        }
      };

      setBuildDraftResponse({ status: "", url: "" });

      if (spec) {
        updObj.collectionSpec = spec;
      }

      // TODO: send object per what object returned from the API
      const [currentPoliciesObject, policiesType] = getPoliciesData(result);
      if (currentPoliciesObject) {
        updObj.policiesData = {
          policiesType,
          collectionInstanceKey,
          policies: {
            live: currentPoliciesObject?.policies?.active,
            draft: currentPoliciesObject?.policies?.draft,
            current: currentPoliciesObject?.policies?.draft,
            local: currentPoliciesObject?.policies?.draft
          },
          spec: {
            live: currentPoliciesObject?.spec?.active,
            draft: currentPoliciesObject?.spec?.draft,
            current: currentPoliciesObject?.spec?.draft
          },
          metadata: {
            tableKeys: currentPoliciesObject?.spec?.active.map(policySpec => policySpec.key),
            metaKeys: currentPoliciesObject?.spec?.active.map(policySpec => policySpec.metadataTableId)
          }
        };
      }

      queryClient.refetchQueries({ queryKey: [FETCH_QUERY_KEYS.DRAFT] });

      const policiesKey = policiesData?.policiesType === POLICIES_TYPES.SCHEDULING ? "schedulingPolicies" : "pbx";
      // Update collection cache directly with the saved data
      queryClient.setQueryData(
        [FETCH_QUERY_KEYS.COLLECTION, selectedAssistant.assistantId, collectionName, collectionKey, collectionInstanceKey],
        (oldData) => ({
          ...oldData,
          draftConfig: updatedCollection,
          validationResults,
          spec: spec || oldData?.spec,
          [policiesKey]: result?.[policiesKey] || oldData?.[policiesKey]
        })
      );

      queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.COLLECTION_REFS] });
      queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.VALIDATION_RESULTS] });
      queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.CHANNEL_SETTINGS] });
      closeSpeechPlayer();
      set(updObj);

      return true;
    } catch (err) {
      set({
        dialogError: GENERIC_ERROR_MESSAGES.SAVE,
        isSavingChanges: false,
        isUpdatingCollection: false,
        isDialogLoading: false
      });
      console.error("Error occurred while configure collection", err);

      return false;
    }
  },
  getChannelSettings: async () => {
    const { selectedAssistant, selectedAccountId } = get();

    try {
      const settings = await assistantsService.fetchChannelSettings({ accountId: selectedAccountId, assistantId: selectedAssistant.assistantId });

      if (settings.draftSettings) {
        set({ draftChannelSettings: settings.draftSettings });
      }

      return settings;
    } catch (e) {
      console.error("Error occurred while fetching channel settings", e);
    }

    return { error: true };
  },
  updateLanguages: async (languages) => {
    const { selectedAssistant, pageName, closeSpeechPlayer } = get();

    try {
      await patch({
        endpoint: `v2/assistants/collections/${selectedAssistant.assistantId}/settings/languages`,
        data: { languages },
        headers: { pageName }
      });
      queryClient.refetchQueries({ queryKey: [FETCH_QUERY_KEYS.DRAFT] });
      queryClient.refetchQueries({ queryKey: [FETCH_QUERY_KEYS.ASSISTANT_LANGUAGES, selectedAssistant.assistantId] });
      queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.CHANNEL_SETTINGS] });
      closeSpeechPlayer();
      queryClient.removeQueries({ queryKey: [FETCH_QUERY_KEYS.COLLECTIONS] });
      queryClient.removeQueries({ queryKey: [FETCH_QUERY_KEYS.COLLECTION] });
      queryClient.removeQueries({ queryKey: [FETCH_QUERY_KEYS.COLLECTION_REFS] });
    } catch (e) {
      set({
        dialogError: GENERIC_ERROR_MESSAGES.SAVE,
      });
      console.error("Error occurred while updating languages", e);
    }
  },
  updateChannelSettings: async () => {
    const { selectedAssistant, selectedAccountId, currentChannelSettings, draft, setBuildDraftResponse, pageName, closeSpeechPlayer } = get();

    try {
      trackEvent(ANALYTICS_EVENTS.GENERAL.PUBLISH_CHANGES);

      await post({
        endpoint: `accounts/${selectedAccountId}/assistants/${selectedAssistant.assistantId}/settings`,
        data: {
          settingsData: {
            settings: { ...currentChannelSettings },
            type: selectedAssistant.channel
          }
        },
        headers: { pageName }
      });

      set({ draftChannelSettings: currentChannelSettings });
      queryClient.refetchQueries({ queryKey: [FETCH_QUERY_KEYS.DRAFT] });
      queryClient.refetchQueries({ queryKey: [FETCH_QUERY_KEYS.ASSISTANT_LANGUAGES, selectedAssistant.assistantId] });
      queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.CHANNEL_SETTINGS] });
      queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.GENERATE_SPEECH] });
      const branchDeployerResponse = await apiGet({ endpoint: `/assistants/${selectedAssistant.assistantId}/build/status?branchName=${draft?.branchName}` });
      setBuildDraftResponse(branchDeployerResponse);
      closeSpeechPlayer();
    } catch (e) {
      set({
        dialogError: GENERIC_ERROR_MESSAGES.SAVE,
      });
      console.error("Error occurred while updating channel settings", e);
    }
  },
  setSelectedCollection: value => set({ selectedCollection: value }),
  setCollectionLoader: value => set({ setCollectionLoader: value }),
  setSelectedCollectionConfig: (config, spec) => {
    const { selectedCollection } = get();
    set({ selectedCollection: { ...config, spec: spec || selectedCollection?.spec } });
  },
  setCollectionConfig: value => set({ collectionConfig: value }),
  updateSliceState: ({ specRef, configRef, draftConfigRef, draft }) => {
    const { collections, infoPopovers, setBuildDraftResponse, closeSpeechPlayer } = get();

    let updatedConfigRef;
    if (draftConfigRef && typeof draftConfigRef === "object" && Object.keys(draftConfigRef).length) {
      updatedConfigRef = draftConfigRef;
    } else if (configRef && typeof configRef === "object") {
      updatedConfigRef = configRef;
    } else {
      updatedConfigRef = {};
    }

    set({
      collections: {
        ...collections,
        specRef,
        configRef: updatedConfigRef,
        currentConfig: updatedConfigRef
      },
      draft,
      infoPopovers: { ...infoPopovers, draftExpiration: true }
    });
    queryClient.refetchQueries({ queryKey: [FETCH_QUERY_KEYS.DRAFT] });
    queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.CHANNEL_SETTINGS] });
    closeSpeechPlayer();
    setBuildDraftResponse({ status: "", url: "" });
  },
  setFailedToUpdateDraft: (value) => {
    set({ failedToUpdateDraft: value });
  },
  clearCollections: () => {
    set({
      collectionConfig: {},
      originalCollectionConfig: {},
      collectionSpec: null,
      selectedCollection: {},
      collections: null,
      isPageLoading: true,
      validationResults: { live: {}, draft: {}, current: {} },
      languages: { live: {}, draft: {}, current: {} },
      policiesData: {
        policies: {
          live: [],
          draft: [],
          current: [],
          local: []
        },
        selectedPolicy: {
          live: null,
          draft: null,
          current: null,
          local: null
        }
      },
      policiesConfigChanged: false
    });
  },

  getConnectivityTestResults: async (path, isSandbox = false) => {
    const { selectedAssistant, collectionName, userData, selectedCollection, pageName } = get();
    const { configuration: { key: collectionKey }, key: collectionInstanceKey } = selectedCollection;
    trackEvent(ANALYTICS_EVENTS.TEST_CONNECTION.CLICKED, {
      selectedAssistantId: selectedAssistant.assistantId,
      user: userData.email
    });

    try {
      const testResults = await post({
        endpoint: `v2/assistants/collections/${selectedAssistant.assistantId}/${getApiCollectionName(collectionName)}/${collectionKey}/${collectionInstanceKey}/${path}/test-connectivity`,
        data: {
          instance_config: selectedCollection,
          userData: { name: userData.name, email: userData.email },
          is_sandbox: isSandbox
        },
        headers: { pageName }
      });

      trackEvent(ANALYTICS_EVENTS.TEST_CONNECTION.SUCCEEDED);

      return testResults;
    } catch (e) {
      trackEvent(ANALYTICS_EVENTS.TEST_CONNECTION.FAILED);
      console.error("Failed to get connectivity test results", e);
      return { error: true };
    }
  },
  getPoliciesData: ({ pbx, schedulingPolicies }) => {
    if (pbx) {
      return [pbx, POLICIES_TYPES.SCREENING];
    }
    if (schedulingPolicies) {
      return [schedulingPolicies, POLICIES_TYPES.SCHEDULING];
    }

    return [];
  }
});

export default createCollectionsSlice;
